import { formatMoney } from "@/utils/settlement";

export const MESSAGE_TYPE = { TEXT: 1, IMAGE: 2, VOICE: 3, SYSTEM: 4, RECEIPT: 5 };

export const SYSTEM_EVENT = {
  ACCOUNT_SENT: "ACCOUNT_SENT",
  RECEIPT_RECORDED: "RECEIPT_RECORDED",
  RECEIPT_REJECTED: "RECEIPT_REJECTED",
  DEADLINE_SOON: "DEADLINE_SOON",
  DEADLINE_PASSED: "DEADLINE_PASSED",
  FULLY_PAID: "FULLY_PAID",
  /** پیام آزاد حسابداری / یادآوری پرداخت — فقط برای گیرنده */
  STAFF_MESSAGE: "STAFF_MESSAGE",
  PAYMENT_REMINDER: "PAYMENT_REMINDER",
  ACCOUNT_REGISTERED: "ACCOUNT_REGISTERED",
};

/** Full name once — family is not repeated when it is already the last word. */
function collapseRepeatedTail(value) {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  while (parts.length >= 2 && parts[parts.length - 1] === parts[parts.length - 2]) parts.pop();
  return parts.join(" ");
}

export function formatAccountHolder(name, family) {
  const holder = collapseRepeatedTail(name);
  const last = collapseRepeatedTail(family);
  if (!holder) return last;
  if (!last) return holder;
  const parts = holder.split(" ");
  if (parts[parts.length - 1] === last) return holder;
  return `${holder} ${last}`;
}

/** Id of the client-side bank-account bot chat (not stored on the server). */
export const BOT_CHAT_ID = "bot";

export function parsePayload(message) {
  if (!message?.payloadJson) return {};
  try {
    return JSON.parse(message.payloadJson) ?? {};
  } catch {
    return {};
  }
}

/** Server-generated texts use Latin digits; user-typed text is never rewritten. */
export function toPersianDigits(text) {
  return String(text ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
}

/** Telegram-style list preview of the last message. */
export function messagePreview(message) {
  if (!message) return "";
  switch (message.type) {
    case MESSAGE_TYPE.IMAGE:
      return message.text ? `🖼 ${message.text}` : "🖼 تصویر";
    case MESSAGE_TYPE.VOICE:
      return "🎤 پیام صوتی";
    case MESSAGE_TYPE.RECEIPT:
      return `🧾 ${toPersianDigits(message.text || "فیش واریزی")}`;
    case MESSAGE_TYPE.SYSTEM:
      return toPersianDigits(message.text);
    default:
      return message.text || "";
  }
}

function toDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatClock(value) {
  const date = toDate(value);
  if (!date) return "";
  return date.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Chat-list timestamp: clock for today, weekday this week, otherwise a short date. */
export function formatListTime(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "";
  if (sameDay(date, now)) return formatClock(date);
  const days = (now.getTime() - date.getTime()) / 86400000;
  if (days < 7) return date.toLocaleDateString("fa-IR", { weekday: "short" });
  return date.toLocaleDateString("fa-IR", { month: "short", day: "numeric" });
}

/** Floating date chip text between message groups. */
export function formatDayChip(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "";
  if (sameDay(date, now)) return "امروز";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return "دیروز";
  return date.toLocaleDateString("fa-IR", { weekday: "long", month: "long", day: "numeric" });
}

/**
 * Splits messages into day sections and marks consecutive messages from the same
 * sender so only the last bubble of a run draws a tail (like Telegram).
 */
export function groupMessages(messages) {
  const sections = [];
  let current = null;
  messages.forEach((message, index) => {
    const date = toDate(message.createDate) ?? new Date();
    if (!current || !sameDay(current.date, date)) {
      current = { key: date.toDateString(), date, items: [] };
      sections.push(current);
    }
    const next = messages[index + 1];
    const nextDate = next ? toDate(next.createDate) : null;
    const isSystem = message.type === MESSAGE_TYPE.SYSTEM;
    const continues =
      next &&
      !isSystem &&
      next.type !== MESSAGE_TYPE.SYSTEM &&
      next.senderPersonId === message.senderPersonId &&
      nextDate &&
      sameDay(nextDate, date);
    current.items.push({ message, isLastInRun: !continues });
  });
  return sections;
}

/**
 * Merges fetched messages into the current list by id, replacing optimistic
 * copies that share a clientMessageId, and keeps ascending order.
 */
export function mergeMessages(current, incoming) {
  if (!incoming?.length) return current;
  const byClientId = new Map(incoming.filter((m) => m.clientMessageId).map((m) => [m.clientMessageId, m]));
  const kept = current.filter((m) => !(m.pending && m.clientMessageId && byClientId.has(m.clientMessageId)));
  const map = new Map(kept.map((m) => [m.id, m]));
  incoming.forEach((m) => map.set(m.id, { ...map.get(m.id), ...m }));
  return [...map.values()].sort((a, b) => {
    // Optimistic messages (string ids) stay at the bottom until confirmed
    const aPending = typeof a.id !== "number";
    const bPending = typeof b.id !== "number";
    if (aPending !== bPending) return aPending ? 1 : -1;
    if (aPending) return String(a.createDate).localeCompare(String(b.createDate));
    return a.id - b.id;
  });
}

export function lastServerId(messages) {
  let max = 0;
  messages.forEach((m) => {
    if (typeof m.id === "number" && m.id > max) max = m.id;
  });
  return max;
}

export function createClientMessageId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "؟";
  // ZWNJ keeps Persian initials as separate letters instead of joining them
  return parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}\u200c${parts[parts.length - 1][0]}`;
}

const AVATAR_COLORS = ["#e17076", "#7bc862", "#65aadd", "#a695e7", "#ee7aae", "#6ec9cb", "#faa774"];

/** Stable Telegram-palette color per conversation. */
export function avatarColor(seed) {
  const text = String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function tomanLabel(value) {
  return `${formatMoney(value)} تومان`;
}

/** Badge text: 1..99, then "99+". */
export function unreadLabel(count) {
  const n = Number(count) || 0;
  if (n <= 0) return "";
  return n > 99 ? "۹۹+" : n.toLocaleString("fa-IR");
}

/** Notification body for a conversation's newest message. */
export function notificationText(conversation) {
  const preview = messagePreview(conversation?.lastMessage);
  return preview || "پیام جدید";
}
